import { ReactNode, useEffect, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useCurrentUser, useCheckAuth } from '../api/authApi';
import { AxiosError } from 'axios';

interface ProtectedRouteProps {
  children: ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated, token, login, logout } = useAuthStore();
  const location = useLocation();
  
  // Check if token exists in localStorage before making API calls (avoid race conditions after login)
  // Memoized to avoid unnecessary localStorage reads on every render
  const hasTokenInStorage = useMemo(() => {
    try {
      const storageData = localStorage.getItem('auth-storage');
      if (storageData) {
        const parsed = JSON.parse(storageData);
        return !!parsed?.state?.token;
      }
    } catch {
      return false;
    }
    return false;
  }, [token]); // Re-check when token changes in Zustand store
  
  // Only check auth if we have a token available (either in store or localStorage)
  const shouldCheckAuth = isAuthenticated && (hasTokenInStorage || !!token);
  
  // First, check if we're already authenticated in the store
  // Only make the API call if we have a token (prevents 401s right after login)
  const { data: isAuthValid, isLoading: isAuthCheckLoading } = useCheckAuth({
    enabled: shouldCheckAuth,
  });
  
  // Then, try to get current user data if we think we're authenticated
  const { 
    data: userData, 
    isLoading: isUserLoading,
    isError: isUserError 
  } = useCurrentUser({
    // Only run this query if we think we're authenticated AND token is available
    enabled: shouldCheckAuth,
    // Handle 401 errors
    onError: (err: AxiosError) => {
      if (err.response?.status === 401) {
        logout();
      }
    }
  });
  
  useEffect(() => {
    // If we get user data back, make sure our store is up to date
    if (userData && isAuthenticated) {
      login(userData.user, userData.token);
    }
  }, [userData, isAuthenticated, login]);

  // Force logout if auth check fails explicitly
  useEffect(() => {
    if (isAuthValid === false && isAuthenticated) {
      logout();
    }
  }, [isAuthValid, isAuthenticated, logout]);
  
  // Safety check: if authenticated but no token, logout to fix inconsistent state
  useEffect(() => {
    if (isAuthenticated && !token && !hasTokenInStorage) {
      console.error('Inconsistent auth state: authenticated but no token found. Logging out.');
      logout();
    }
  }, [isAuthenticated, token, hasTokenInStorage, logout]);

  // Loading state: show loading while auth checks are in progress
  // Only show loading if we're checking auth (have a token) and the checks are still running
  const isLoading = shouldCheckAuth && (isAuthCheckLoading || isUserLoading);

  if (isLoading) {
    return <div className="flex items-center justify-center h-screen">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
        <p className="mt-4 text-lg">Authenticating...</p>
      </div>
    </div>;
  }

  // If not authenticated or auth error, redirect to login
  if (!isAuthenticated || isUserError || isAuthValid === false) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Render children if authenticated
  return <>{children}</>;
}; 